import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import FieldHint from "../components/FieldHint";
import OrganizerFeesCard from "../components/OrganizerFeesCard";
import { api, ApiError, openAuthenticatedFile, type AdminFinanceDashboardResponse, type AdminFinanceOrderRow } from "../lib/api";
import { formatDateTime } from "../lib/dateFormat";
import { membershipPaymentStatusLabel, paymentProviderLabel, paymentStatusLabel } from "../lib/labels";
import { formatMoney } from "../lib/money";
import { useSignedLinkRefresh } from "../lib/useSignedLinkRefresh";
import "./CuentasCobro.css";

// Club en pausa (Eduardo 2026-10-08): se ocultan filtros, renglones,
// contadores y acciones de membresías/club. El código se queda; para volver
// a mostrarlo basta con poner esto en true.
const CLUB_FINANCE_VISIBLE = false;

const STATUS_CHIPS: Array<[string, string]> = [
  ["", "Todos"],
  ["paid", "Pagados"],
  ["pending", "Pendientes"],
  ["receipt_submitted", "Comprobante enviado"],
  ["rejected", "Rechazados"],
  ["cancelled", "Cancelados"],
  ["refunded", "Reembolsados"],
];

const PERIOD_CHIPS: Array<[string, string]> = [
  ["today", "Hoy"],
  ["this_month", "Este mes"],
  ["this_year", "Este año"],
  ["all", "Todo"],
  ["custom", "Fechas…"],
];

function money(currency: string, value: string | number | null | undefined): string {
  return formatMoney(value, currency || "MXN");
}

function dateTime(value: string | null): string {
  return formatDateTime(value);
}

function statusLabel(order: AdminFinanceOrderRow): string {
  if (order.source === "club_membership") {
    return membershipPaymentStatusLabel(order.raw_status ?? order.status);
  }
  return paymentStatusLabel(order.status);
}

const PAGE_SIZE = 50;

export function FinancePager({
  page,
  totalPages,
  total,
  pageSize,
  onChange,
  compact = false,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
  compact?: boolean;
}) {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <nav className={`adm-fin__pager${compact ? " adm-fin__pager--compact" : ""}`} aria-label={compact ? "Paginación (arriba)" : "Paginación"}>
      <span className="adm-fin__pager-range">{from}–{to} de {total}</span>
      {totalPages > 1 ? (
        <span className="adm-fin__pager-nav">
          <button type="button" className="btn btn--compact btn--ghost" disabled={page <= 1} onClick={() => onChange(page - 1)}>Anterior</button>
          <span className="adm-fin__pager-page">Página {page} de {totalPages}</span>
          <button type="button" className="btn btn--compact btn--ghost" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Siguiente</button>
        </span>
      ) : null}
    </nav>
  );
}

function statusClass(order: AdminFinanceOrderRow): string {
  if (order.status === "paid") return "active";
  if (order.status === "pending") return "draft";
  return "closed";
}

function OrderRow({
  order,
  busyOrderId,
  onClubOrderDecision,
  onViewReceipt,
  onCopyReminder,
}: {
  order: AdminFinanceOrderRow;
  busyOrderId: number | null;
  onClubOrderDecision: (orderId: number, action: "confirm" | "reject") => void;
  onViewReceipt: (order: AdminFinanceOrderRow) => void;
  onCopyReminder: (order: AdminFinanceOrderRow) => void;
}) {
  const target = order.source === "club_membership"
    ? order.club_id
      ? `/club?tab=pagos`
      : undefined
    : order.tournament_id
      ? `/admin/tournaments/${order.tournament_id}/registrations`
      : undefined;
  return (
    <tr>
      <td data-label="Jugador / Socio">
        <strong>{order.player_name}</strong>
        <small>{dateTime(order.created_at)}</small>
      </td>
      <td data-label="Concepto">
        <span>{order.concept || order.source_label}</span>
        <small>
          {target ? <Link to={target}>{order.tournament_name ?? order.club_name ?? "—"}</Link> : (order.tournament_name ?? order.club_name ?? "—")}
        </small>
        {(order.items?.length ?? 0) > 1 && (
          <small>
            Cubre: {(order.items ?? [])
              .map((item) => `${item.division_name ?? "Sin categoría"} ${money(order.currency, item.amount)}`)
              .join(" · ")}
          </small>
        )}
      </td>
      <td data-label="Cobro">{paymentProviderLabel(order.provider)}</td>
      <td data-label="Cuenta" className="finance-cell-account">{order.account_label || "—"}</td>
      <td data-label="Monto">
        <strong>{money(order.currency, order.gross_amount)}</strong>
        <small>Neto {money(order.currency, order.net_amount ?? order.organizer_net_amount)}</small>
        <small>
          {order.provider_fee_amount != null ? `MP ${money(order.currency, order.provider_fee_amount)} · ` : ""}
          T123 {money(order.currency, order.platform_fee_amount)}
        </small>
      </td>
      <td data-label="Estado">
        <span className={`adm-fin__status adm-fin__status--${order.raw_status === "receipt_submitted" || order.status === "receipt_submitted" ? "receipt" : statusClass(order)}`}>{statusLabel(order)}</span>
        {order.paid_at ? <small>{dateTime(order.paid_at)} · orden {order.id}</small> : <small>Orden #{order.id}</small>}
      </td>
      <td data-label="Acciones">
        <div className="table-actions adm-fin__actions">
          {order.receipt_url ? (
            order.source === "club_membership" ? (
              <button type="button" className="btn btn--compact btn--ghost" onClick={() => onViewReceipt(order)}>Ver comprobante</button>
            ) : (
              <a className="btn btn--compact btn--ghost" href={order.receipt_url} target="_blank" rel="noreferrer">Ver comprobante</a>
            )
          ) : null}
          {order.source === "tournament_registration" && order.payment_reminder ? (
            <>
              {order.payment_link ? (
                <a className="btn btn--compact btn--ghost" href={order.payment_link} target="_blank" rel="noreferrer">
                  Abrir link
                </a>
              ) : null}
              <button type="button" className="btn btn--compact" onClick={() => onCopyReminder(order)}>
                {order.provider === "manual" ? "Copiar recordatorio" : "Copiar link de pago"}
              </button>
            </>
          ) : null}
          {order.source === "club_membership" && order.raw_status === "receipt_submitted" ? (
            <>
              <button type="button" className="btn btn--compact adm-fin__confirm" disabled={busyOrderId === order.id} onClick={() => onClubOrderDecision(order.id, "confirm")}>Confirmar</button>
              <button type="button" className="btn btn--compact btn--ghost" disabled={busyOrderId === order.id} onClick={() => onClubOrderDecision(order.id, "reject")}>Rechazar</button>
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export default function AdminFinanceDashboardPage() {
  const { token } = useAuth();
  const { askConfirm } = useAbortableConfirm([]);
  const [data, setData] = useState<AdminFinanceDashboardResponse["data"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tournamentId, setTournamentId] = useState("");
  const [source, setSource] = useState("all");
  const [clubId, setClubId] = useState("");
  const [sociosFilter, setSociosFilter] = useState("");
  // El inicio admin abre Finanzas ya filtrado (?status=pending&period=all)
  // para «Comprobantes por revisar».
  const [searchParams] = useSearchParams();
  const [period, setPeriod] = useState(() => searchParams.get("period") || "this_month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [provider, setProvider] = useState("");
  // Cuentas de cobro F1: filtro «Cuenta» ("mp" o "bank:<id>").
  const [accountFilter, setAccountFilter] = useState("");
  const [paymentAccounts, setPaymentAccounts] = useState<{ mpConnected: boolean; banks: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  // La búsqueda va al servidor (todas las páginas), con una pausa corta.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [searchTerm]);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [busyOrderId, setBusyOrderId] = useState<number | null>(null);

  // Con el club en pausa se piden solo cobros de torneos (filtro «Origen»
  // existente), así los totales del servidor no mezclan membresías.
  const effectiveSource = CLUB_FINANCE_VISIBLE ? source : "tournament_registration";
  const filterParams = useMemo(() => ({
    tournament_id: effectiveSource !== "club_membership" && tournamentId ? Number(tournamentId) : undefined,
    source: effectiveSource === "all" ? undefined : effectiveSource,
    club_id: effectiveSource !== "tournament_registration" && clubId ? Number(clubId) : undefined,
    player_profile_id: sociosFilter ? Number(sociosFilter) : undefined,
    period,
    date_from: period === "custom" ? dateFrom : undefined,
    date_to: period === "custom" ? dateTo : undefined,
    status: status || undefined,
    provider: provider || undefined,
    account: accountFilter || undefined,
    q: debouncedSearch || undefined,
  }), [accountFilter, clubId, dateFrom, dateTo, debouncedSearch, effectiveSource, period, provider, sociosFilter, status, tournamentId]);

  // Tarjeta «Cobros con Mercado Pago» (mismo acceso que la del Inicio).
  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.payments
      .account(token)
      .then((response) => {
        if (!alive) return;
        const mp = response.data.mercadopago;
        setPaymentAccounts({
          mpConnected: Boolean(mp?.configured) && mp?.account?.account_status === "active",
          banks: (response.data.bank_accounts ?? []).length,
        });
      })
      .catch(() => {
        if (alive) setPaymentAccounts(null);
      });
    return () => {
      alive = false;
    };
  }, [token]);
  // Cualquier cambio de filtro regresa a la página 1.
  useEffect(() => {
    setPage(1);
  }, [filterParams]);
  const params = useMemo(() => ({ ...filterParams, page, page_size: PAGE_SIZE }), [filterParams, page]);

  const latestLoadRef = useRef(0);
  // `silent`: recarga de fondo para renovar las ligas de los comprobantes
  // (vencen en una hora): sin spinner ni mensajes, y si falla se queda lo
  // que ya se ve. Si mientras tanto empieza una carga normal (otro filtro),
  // el resultado de fondo se descarta; la carga normal nunca se descarta
  // por una de fondo.
  const loadDashboard = useCallback(async (options?: { silent?: boolean }) => {
    if (!token) return;
    const silent = options?.silent === true;
    const attempt = silent ? latestLoadRef.current : ++latestLoadRef.current;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await api.payments.finance(token, params);
      if (attempt === latestLoadRef.current) setData(response.data);
    } catch (err) {
      if (!silent && attempt === latestLoadRef.current) {
        setError(err instanceof Error ? err.message : "No se pudo cargar el dashboard financiero.");
      }
    } finally {
      if (!silent && attempt === latestLoadRef.current) setLoading(false);
    }
  }, [params, token]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  // «Comprobante» abre una liga firmada en otra pestaña. Si la pantalla se
  // queda abierta, se piden ligas nuevas antes de que venzan (al volver a la
  // pestaña o cada minuto): el clic siguiente ya no cae en «liga vencida».
  const receiptLinks = useMemo(
    () => (data?.orders ?? []).map((order) => (order.source === "club_membership" ? null : order.receipt_url)),
    [data?.orders],
  );
  const refreshReceiptLinks = useCallback(() => loadDashboard({ silent: true }), [loadDashboard]);
  useSignedLinkRefresh(receiptLinks, refreshReceiptLinks);

  const summary = data?.summary;
  const currency = data?.orders[0]?.currency ?? "MXN";
  const socioOptions = useMemo(() => {
    const seen = new Map<number, string>();
    (data?.orders ?? []).forEach((order) => {
      if (order.source !== "club_membership" || !order.player_profile_id) return;
      if (!seen.has(order.player_profile_id)) seen.set(order.player_profile_id, order.player_name);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], "es"));
  }, [data?.orders]);
  const visibleOrders = useMemo(
    () => (data?.orders ?? []).filter((order) => CLUB_FINANCE_VISIBLE || order.source !== "club_membership"),
    [data?.orders],
  );
  const pagination = data?.pagination ?? { page: 1, page_size: PAGE_SIZE, total: visibleOrders.length, total_pages: 1 };

  // Conteos de los chips de estado: los del servidor con «Todos» (sin filtro
  // de estado); al elegir un chip se conservan los últimos de «Todos».
  const [statusCounts, setStatusCounts] = useState<{ total: number; breakdown: Record<string, number> } | null>(null);
  useEffect(() => {
    if (data && !data.filters?.status) {
      setStatusCounts({ total: data.summary.total_orders, breakdown: data.summary.status_breakdown ?? {} });
    }
  }, [data]);
  const receiptsWaiting = statusCounts?.breakdown.receipt_submitted ?? 0;
  // Totales del filtro completo (todas las páginas), calculados en el servidor.
  const filterTotal = summary?.filter_gross ?? "0";
  const byProvider = {
    mercadopago_marketplace: summary?.paid_by_provider?.mercadopago_marketplace ?? "0",
    stripe_connect: summary?.paid_by_provider?.stripe_connect ?? "0",
    manual: summary?.paid_by_provider?.manual ?? "0",
  };

  async function handleExport() {
    if (!token) return;
    setExporting(true);
    setError(null);
    try {
      const response = await api.payments.downloadFinanceXlsx(token, filterParams);
      if (!response.ok) throw new Error("No se pudo generar el Excel.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `finanzas-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo generar el Excel.");
    } finally {
      setExporting(false);
    }
  }

  async function handleCopyReminder(order: AdminFinanceOrderRow) {
    const text = order.payment_reminder ?? order.payment_link ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setMessage(
        order.provider === "manual"
          ? `Recordatorio con datos bancarios copiado para ${order.player_name}. Pégalo en WhatsApp o correo.`
          : `Link de pago copiado para ${order.player_name}. Pégalo en WhatsApp o correo.`,
      );
      setError(null);
    } catch {
      setError("No se pudo copiar al portapapeles. Usa 'Abrir link' y copia la URL.");
    }
  }

  async function handleViewReceipt(order: AdminFinanceOrderRow) {
    if (!token || !order.receipt_url) return;
    try {
      await openAuthenticatedFile(order.receipt_url, token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo abrir el comprobante.");
    }
  }

  async function handleClubOrderDecision(orderId: number, action: "confirm" | "reject") {
    if (!token) return;
    const confirmed = await askConfirm(action === "confirm" ? "¿Confirmar pago y activar al socio?" : "¿Rechazar comprobante y pedir otro?", { acceptLabel: action === "confirm" ? "Sí, confirmar pago" : "Sí, rechazar comprobante", tone: action === "confirm" ? "default" : "danger" });
    if (!confirmed) return;
    setBusyOrderId(orderId);
    setError(null);
    setMessage(null);
    try {
      if (action === "confirm") {
        await api.clubs.confirmMembershipPaymentOrder(token, orderId);
        setMessage("Pago de club confirmado. El socio quedó activo.");
      } else {
        await api.clubs.rejectMembershipPaymentOrder(token, orderId);
        setMessage("Comprobante rechazado. El socio podrá subir otro.");
      }
      await loadDashboard();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo actualizar el cobro de club.");
    } finally {
      setBusyOrderId(null);
    }
  }

  return (
    <section className="adm-players adm-fin admin-finance-dashboard-page">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Finanzas · Cobros</span>
          <h1 className="adm-players__title">Finanzas</h1>
        </div>
        <div className="adm-players__chips" role="group" aria-label="Periodo">
          {PERIOD_CHIPS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`adm-players__filter${period === key ? " is-active" : ""}`}
              aria-pressed={period === key}
              onClick={() => setPeriod(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </header>
      {period === "custom" ? (
        <div className="adm-fin__dates">
          <label>Desde<input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label>Hasta<input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        </div>
      ) : null}

      {paymentAccounts ? (
        <div className="finance-cobros-card">
          <div className="finance-cobros-card__text">
            <strong>Cobros con Mercado Pago</strong>
            <span className="finance-cobros-card__status">
              {paymentAccounts.mpConnected ? "Cuenta conectada ✓" : "Mercado Pago sin conectar"}
            </span>
            <span className="finance-cobros-card__sub">
              Transferencias: {paymentAccounts.banks === 1 ? "1 cuenta bancaria" : `${paymentAccounts.banks} cuentas bancarias`}
            </span>
          </div>
          <Link className="btn finance-cobros-card__cta" to="/admin/payments">Administrar cuentas</Link>
        </div>
      ) : null}

      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {message ? <p className="form-message">{message}</p> : null}

      <div className="adm-fin__summary">
        <div className="adm-fin__net">
          <span className="adm-fin__label hint-label">
            Neto para ti
            <FieldHint label="Qué es el neto">Lo que te queda: pagos recibidos menos la comisión Torneos123 y, en cobros por Mercado Pago, menos su comisión y lo devuelto en reembolsos parciales. Es lo que llega a tu cuenta.</FieldHint>
          </span>
          <strong className="adm-fin__net-value">{money(currency, summary?.net_received ?? summary?.organizer_net_received)}</strong>
          <small>Lo que te queda después de comisiones</small>
          {Number(summary?.refunded_partial ?? 0) > 0 ? (
            <small>Incluye {money(currency, summary?.refunded_partial)} devueltos en reembolsos parciales.</small>
          ) : null}
        </div>
        <div className="adm-fin__metrics">
          <div className="adm-fin__metric">
            <span className="adm-fin__label hint-label">
              Pagos recibidos
              <FieldHint label="Qué son los pagos recibidos">Suma de las órdenes ya pagadas en el periodo y filtros elegidos (por Mercado Pago o marcadas como pagadas a mano). Es el monto bruto que pagó el jugador o socio.</FieldHint>
            </span>
            <strong>{money(currency, summary?.gross_received)}</strong>
            <small>{summary?.paid_orders ?? 0} pagos</small>
          </div>
          <div className="adm-fin__metric adm-fin__metric--lime">
            <span className="adm-fin__label hint-label">
              Por cobrar
              <FieldHint label="Qué es pendiente por cobrar">Inscripciones y membresías con orden pendiente: el jugador no ha pagado o su comprobante no se ha confirmado.</FieldHint>
            </span>
            <strong>{money(currency, summary?.pending_gross)}</strong>
            <small>{summary?.pending_orders ?? 0} pendientes</small>
          </div>
          <div className="adm-fin__metric" data-fee-mp>
            <span className="adm-fin__label hint-label">
              Fee Torneos123 · Mercado Pago
              <FieldHint label="Qué es el fee cobrado por Mercado Pago">Fee de Torneos123 de las inscripciones cobradas con Mercado Pago. Ya se descontó en cada cobro: no lo debes.</FieldHint>
            </span>
            <strong>{money(currency, summary?.platform_fee_mp ?? summary?.platform_fee_received)}</strong>
            <small>Ya descontado</small>
          </div>
          <div className="adm-fin__metric" data-fee-outside>
            <span className="adm-fin__label hint-label">
              Fee Torneos123 · por fuera
              <FieldHint label="Qué es el fee por fuera">Fee de Torneos123 de las inscripciones que cobraste tú (efectivo, transferencia o «Pagado» a mano). Ese lo debes: págalo en «Fees de Torneos123».</FieldHint>
            </span>
            <strong>{money(currency, summary?.platform_fee_outside ?? "0")}</strong>
            <small>Lo debes a Torneos123</small>
          </div>
          <div className="adm-fin__metric">
            <span className="adm-fin__label hint-label">
              Comisión Mercado Pago
              <FieldHint label="Qué es la comisión Mercado Pago">Lo que Mercado Pago descontó en los cobros en línea del periodo (3.49% + $4 + IVA por cobro al instante), según lo reporta cada pago aprobado. En pagos por fuera no aplica.</FieldHint>
            </span>
            <strong>{money(currency, summary?.provider_fee_received)}</strong>
          </div>
        </div>
      </div>

      <OrganizerFeesCard />

      {receiptsWaiting > 0 ? (
        <div className="adm-fin__notice" role="status">
          <span>
            {receiptsWaiting === 1 ? "1 comprobante de pago espera tu revisión" : `${receiptsWaiting} comprobantes de pago esperan tu revisión`}
          </span>
          <button type="button" className="adm-fin__notice-btn" onClick={() => setStatus("receipt_submitted")}>
            Revisar ahora
          </button>
        </div>
      ) : null}

      <div className="adm-players__panel">
        <div className="adm-players__toolbar">
          <label className="adm-players__search">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              aria-label="Buscar"
              placeholder={CLUB_FINANCE_VISIBLE ? "Jugador, socio, torneo o club" : "Jugador, torneo u orden"}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
          {CLUB_FINANCE_VISIBLE ? (
            <select aria-label="Origen" className="adm-tourn__select" value={source} onChange={(event) => {
              setSource(event.target.value);
              setTournamentId("");
              setClubId("");
              setSociosFilter("");
              setProvider("");
            }}>
              <option value="all">Todos los orígenes</option>
              <option value="tournament_registration">Torneos</option>
              <option value="club_membership">Club / Membresías</option>
            </select>
          ) : null}
          {effectiveSource !== "club_membership" ? (
            <select aria-label="Torneo" className="adm-tourn__select" value={tournamentId} onChange={(event) => setTournamentId(event.target.value)}>
              <option value="">Todos los torneos</option>
              {data?.tournaments.map((tournament) => (
                <option key={tournament.id} value={tournament.id}>{tournament.name}</option>
              ))}
            </select>
          ) : null}
          {CLUB_FINANCE_VISIBLE && effectiveSource !== "tournament_registration" ? (
            <>
              <select aria-label="Club" className="adm-tourn__select" value={clubId} onChange={(event) => setClubId(event.target.value)}>
                <option value="">Todos los clubes</option>
                {(data?.clubs ?? []).map((club) => (
                  <option key={club.id} value={club.id}>{club.name}</option>
                ))}
              </select>
              <select aria-label="Socio" className="adm-tourn__select" value={sociosFilter} onChange={(event) => setSociosFilter(event.target.value)}>
                <option value="">Todos los socios</option>
                {socioOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </>
          ) : null}
          <select aria-label="Cobro" className="adm-tourn__select" value={provider} onChange={(event) => setProvider(event.target.value)}>
            <option value="">Todas las formas de cobro</option>
            <option value="manual">Por fuera</option>
            {CLUB_FINANCE_VISIBLE ? <option value="manual_club">Club manual</option> : null}
            <option value="stripe_connect">Stripe</option>
            <option value="mercadopago_marketplace">Mercado Pago</option>
          </select>
          <select aria-label="Cuenta" className="adm-tourn__select" value={accountFilter} onChange={(event) => setAccountFilter(event.target.value)}>
            <option value="">Todas las cuentas</option>
            {accountFilter && !(data?.accounts ?? []).some((option) => option.key === accountFilter) ? (
              <option value={accountFilter}>Cuenta elegida</option>
            ) : null}
            {(data?.accounts ?? []).map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
          <button className="btn btn--ghost" type="button" onClick={() => void loadDashboard()} disabled={loading}>Actualizar</button>
          <button className="btn btn--ghost" type="button" onClick={() => void handleExport()} disabled={exporting || loading || pagination.total === 0}>
            {exporting ? "Generando…" : "Exportar a Excel"}
          </button>
        </div>
        <div className="adm-players__chips" role="group" aria-label="Filtrar por estado">
          {STATUS_CHIPS.map(([key, label]) => {
            const count = statusCounts ? (key ? statusCounts.breakdown[key] ?? 0 : statusCounts.total) : null;
            return (
              <button
                key={key || "all"}
                type="button"
                className={`adm-players__filter${status === key ? " is-active" : ""}`}
                aria-pressed={status === key}
                onClick={() => setStatus(key)}
              >
                {label}{count !== null ? <span className="adm-fin__chip-count"> {count}</span> : null}
              </button>
            );
          })}
        </div>
        {!loading && pagination.total_pages > 1 ? (
          <FinancePager compact page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} pageSize={pagination.page_size} onChange={setPage} />
        ) : null}

        <div className="table-wrap adm-players__table-shell adm-fin__table-wrap">
          <table className="data-table adm-fin__table">
            <thead>
              <tr>
                <th>Jugador{CLUB_FINANCE_VISIBLE ? " / Socio" : ""}</th>
                <th>Concepto</th>
                <th>Cobro</th>
                <th>Cuenta</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}>Cargando dashboard financiero…</td></tr>
              ) : data && visibleOrders.length > 0 ? (
                visibleOrders.map((order) => <OrderRow key={`${order.source}-${order.id}`} order={order} busyOrderId={busyOrderId} onClubOrderDecision={handleClubOrderDecision} onViewReceipt={handleViewReceipt} onCopyReminder={handleCopyReminder} />)
              ) : (
                <tr><td colSpan={7}>No hay órdenes con esos filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {!loading ? (
          <FinancePager page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} pageSize={pagination.page_size} onChange={setPage} />
        ) : null}
        <p className="adm-players__footer muted">
          Total del filtro: <strong>{money(currency, filterTotal)}</strong> · {pagination.total} {pagination.total === 1 ? "cobro" : "cobros"}
        </p>
      </div>

      <div className="adm-players__panel">
        <div className="adm-fin__accounts-head">
          <span className="adm-players__eyebrow">Cuentas de cobro</span>
          <Link to="/admin/payments">Configurar</Link>
        </div>
        <div className="adm-fin__accounts">
          <div className="adm-fin__account"><small>Mercado Pago</small><strong>{money(currency, byProvider.mercadopago_marketplace)}</strong></div>
          <div className="adm-fin__account"><small>Stripe</small><strong>{money(currency, byProvider.stripe_connect)}</strong></div>
          <div className="adm-fin__account"><small>Por fuera</small><strong>{money(currency, byProvider.manual)}</strong></div>
        </div>
        <small className="muted">Pagos confirmados de todos los cobros del filtro.</small>
      </div>
    </section>
  );
}
